import React from 'react';
import { useHistory } from 'react-router-dom';
import { addRouteNamespace } from '@Utils/actions';
import {
	getSubscriptions,
	type MenuItem,
	PropertyCard,
	success,
	error,
	Text,
} from '@innovyze/stylovyze';
import {
	AddToPhotos,
	Delete,
	Visibility,
	VisibilityOff,
	CheckCircle,
	RemoveCircle,
} from '@mui/icons-material';
import { type RehabTreePublishStateType } from '@innovyze/lib-am-common';
import { formatDateTimeWithFormatDate } from '@innovyze/lib-am-common/Utils';
import { toggleRehabTreePublishState } from '@innovyze/lib-am-common/Actions';
import './agSet-card.css';
import { fullAccess } from '@innovyze/shared-utils';
import { duplicateRehabTree, setInitalLoadSet } from '@Actions';
import { useDispatch } from 'react-redux';
import { useGlobalization } from '@Translations';
import {
	AGSetMode,
	getTreeSetupMode,
	type RehabCard,
} from '@Types/rehab.types';
import { Link, Stack, Tooltip } from '@mui/material';
import { green, grey } from '@mui/material/colors';
import { useCompanyDateTime } from '@innovyze/stylovyze';
import {
	useAssetTypeSummary,
	useSystemType,
} from '../../hooks/assetSchemaHooks';

interface Props {
	agSetTree: RehabCard;
	onDeleteTree: (id: string) => void;
	disabled: boolean;
	onRefresh: () => void;
}

const AGSetCard = ({ agSetTree, onDeleteTree, disabled, onRefresh }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();
	const subscriptions = getSubscriptions();
	const hasFullAccess = fullAccess(subscriptions);
	const { formatDate } = useCompanyDateTime();

	const assetTypeSummary = useAssetTypeSummary(agSetTree.assetType?.id ?? '');
	const systemTypeResult = useSystemType(
		assetTypeSummary.status === 'success'
			? assetTypeSummary.data.systemTypeId
			: '',
	);
	// Falls back to '-' while loading, or if either type can't be resolved.
	const systemAndAssetType =
		assetTypeSummary.status === 'success' &&
		systemTypeResult.status === 'success'
			? t('{{systemType}} - {{assetType}}', {
					systemType: t(systemTypeResult.data.displayName),
					assetType: t(assetTypeSummary.data.displayName),
				})
			: '-';

	const handleViewClick = () => {
		if (!disabled) {
			dispatch(setInitalLoadSet(true));
			history.push(addRouteNamespace(`details/${agSetTree._id}`));
		}
	};

	const handleDeleteClick = () => {
		if (!disabled) onDeleteTree(agSetTree._id);
	};

	const onDuplicateComplete = (treeId: string) => {
		if (treeId) {
			dispatch(setInitalLoadSet(true));
			history.push(addRouteNamespace(`details/${treeId}`));
		}
	};

	const handleDuplicateClick = () => {
		if (!disabled)
			dispatch(
				duplicateRehabTree({
					treeId: agSetTree._id,
					onComplete: onDuplicateComplete,
				}),
			);
	};

	const notifyagSetTreeStateChange = (
		newPublishState: RehabTreePublishStateType,
	) => {
		if (newPublishState === 'published') {
			dispatch(
				success(
					t(
						// eslint-disable-next-line prettier/prettier
						'Asset Group Set has been published and it\'s ready for analysis.',
					),
				),
			);
		} else {
			dispatch(success(t('Asset Group Set unpublished.')));
		}
		onRefresh();
	};

	const handlePublishToggle = (
		newPublishState: RehabTreePublishStateType,
	) => {
		dispatch(
			toggleRehabTreePublishState({
				treeId: agSetTree._id,
				cb: () => notifyagSetTreeStateChange(newPublishState),
				newPublishState,
			}),
		);
	};

	const formatUsername = (username: string) => {
		if (username.indexOf('@') !== -1) {
			return username.substring(0, username.lastIndexOf('@'));
		}
		return username;
	};

	const menuItems = () => {
		const items: MenuItem[] = [];

		if (hasFullAccess) {
			items.push({
				onClick: handleDuplicateClick,
				text: t('Duplicate'),
				icon: <AddToPhotos />,
			});
			items.push({
				onClick: handleDeleteClick,
				text: t('Delete'),
				icon: <Delete />,
			});
			items.push({
				onClick: () => {
					if (agSetTree.publishState === 'published') {
						handlePublishToggle('unpublished');
					}
					if (agSetTree.publishState === 'unpublished') {
						if (!agSetTree.lastRun) {
							dispatch(
								error(
									t(
										'Unable to publish. Please run the Asset Group Set before publishing.',
									),
								),
							);
							return;
						}
						handlePublishToggle('published');
					}
				},
				text:
					agSetTree.publishState === 'unpublished'
						? t('Publish')
						: t('Unpublish'),
				icon:
					agSetTree.publishState === 'unpublished' ? (
						<Visibility />
					) : (
						<VisibilityOff />
					),
			});
		}
		return items;
	};

	const renderHeader = () => {
		let tooltipText =
			agSetTree.publishState === 'published'
				? t('Published')
				: t('Unpublished');

		if (agSetTree.publishedBy) {
			tooltipText += t(' By: {{publishedBy}} - On: {{publishedAt}}', {
				publishedBy: formatUsername(agSetTree.publishedBy),
				publishedAt: formatDateTimeWithFormatDate(
					agSetTree.publishedAt,
					formatDate,
				),
			});
		}

		const icon =
			agSetTree.publishState === 'published' ? (
				<CheckCircle sx={{ color: green[500] }} />
			) : (
				<RemoveCircle sx={{ color: grey[500] }} />
			);

		const publishStateTooltip = (
			<Tooltip title={tooltipText}>{icon}</Tooltip>
		);
		return (
			<div className="header-container">
				{renderTitle()} {publishStateTooltip}
			</div>
		);
	};

	const renderTitle = () => {
		if (hasFullAccess || agSetTree.publishState === 'published') {
			return getTitle();
		}
		return (
			<div
				style={{
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					width: '100%',
					whiteSpace: 'nowrap',
				}}
				data-cy="card-title">
				{agSetTree.name}
				<div
					style={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						width: '100%',
						whiteSpace: 'nowrap',
					}}
					data-cy="card-description">
					<Text variant="text-medium">
						{agSetTree.description &&
						agSetTree.description.length !== 0
							? agSetTree.description
							: '\u00A0'}
					</Text>
				</div>
			</div>
		);
	};

	const getTitle = () => {
		return (
			<Stack
				style={{
					width: '100%',
				}}>
				<Link
					underline="hover"
					style={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						cursor: 'pointer',
						width: '100%',
						whiteSpace: 'nowrap',
					}}
					title={agSetTree.name}
					onClick={() => {
						handleViewClick();
					}}
					data-cy="card-title">
					{agSetTree.name}
				</Link>
				<div
					style={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						width: '100%',
						whiteSpace: 'nowrap',
					}}
					data-cy="card-description">
					<Text variant="text-medium">
						{agSetTree.description &&
						agSetTree.description.length !== 0
							? agSetTree.description
							: '\u00A0'}
					</Text>
				</div>
			</Stack>
		);
	};

	return (
		<div
			key={agSetTree._id}
			style={{
				fontSize: '1rem',
			}}>
			<PropertyCard
				columns={3}
				forceMenu
				className="adjusted-menu-position-rehab"
				menuItems={menuItems()}
				properties={[
					{
						title: t('Last Run'),
						value: `${
							agSetTree.lastRun
								? formatDateTimeWithFormatDate(
										agSetTree.lastRun,
										formatDate,
									)
								: '-'
						}`,
						ellipsis: true,
						span: 2,
					},
					{
						title: t('Status'),
						type: agSetTree.status ? 'status' : 'text',
						value: `${agSetTree.status ? agSetTree.status : '-'}`,
					},
					{
						title: t('Run By'),
						value: `${
							agSetTree.runBy
								? formatUsername(agSetTree.runBy)
								: '-'
						}`,
						ellipsis: true,
						span: 2,
					},
					{
						title: t('Groups Count'),
						value: `${
							agSetTree.groupCount ? agSetTree.groupCount : '-'
						}`,
					},
					{
						title: t('System and Asset Type'),
						value: systemAndAssetType,
						ellipsis: true,
						span: 2,
					},
					{
						title: t('Setup'), // TODO - Update to setup type
						value: getTreeSetupMode(
							agSetTree.mode ?? AGSetMode.AUTOMATIC,
							t,
						),
					},
					{
						title: t('Created On'),
						value: `${
							agSetTree.createdAt
								? formatDateTimeWithFormatDate(
										agSetTree.createdAt,
										formatDate,
									)
								: '-'
						}`,
						ellipsis: true,
						span: 2,
					},
					{
						title: t('Created By'),
						value: `${
							agSetTree.createdBy
								? formatUsername(agSetTree.createdBy)
								: '-'
						}`,
						ellipsis: true,
					},
					{
						title: t('Updated On'),
						value: `${
							agSetTree.updatedAt
								? formatDateTimeWithFormatDate(
										agSetTree.updatedAt,
										formatDate,
									)
								: '-'
						}`,
						ellipsis: true,
						span: 2,
					},
					{
						title: t('Updated By'),
						value: `${
							agSetTree.updatedBy
								? formatUsername(agSetTree.updatedBy)
								: '-'
						}`,
						ellipsis: true,
					},
				]}
				title={renderHeader()}
			/>
		</div>
	);
};
export default AGSetCard;
